// src/hooks/useScrollReveal.ts
import { RefObject, useEffect } from "react";

export interface RevealOptions {
  selectorList: string[];
  maxBlur?: number;
  gapFactor?: number;
}

export function useScrollReveal(
  containerRef: React.RefObject<HTMLElement | null>,
  options: RevealOptions
) {
  const { selectorList, maxBlur = 8, gapFactor = 1 } = options;

  useEffect(() => {
    const handle = () => {
      const el = containerRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const windowH = window.innerHeight;
      const raw = 1 - Math.max(0, Math.min(1, rect.top / windowH));
      const progress = Math.max(0, Math.min(1, raw));

      const elems: HTMLElement[] = selectorList
        .map((sel) => Array.from(el.querySelectorAll<HTMLElement>(sel)))
        .flat();

      const total = elems.length;
      const step = 1 / (total * gapFactor);

      elems.forEach((child, idx) => {
        const start = idx * step;
        const end = (idx + 1) * step;
        const local = Math.max(
          0,
          Math.min(1, (progress - start) / (end - start))
        );
        child.style.filter = `blur(${(1 - local) * maxBlur}px)`;
        child.style.opacity = `${local}`;
      });
    };

    window.addEventListener("scroll", handle, { passive: true });
    handle();
    return () => void window.removeEventListener("scroll", handle);
  }, [containerRef, selectorList, maxBlur, gapFactor]);
}
